// src/components/social/CreateProfileModal.jsx
// Creation du profil Fynho Social : photo, username unique, ville/quartier, positionnement carte

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import { useTheme } from '../../hooks/useTheme';
import * as socialApi from '../../utils/socialApi';
import apiClient from '../../api/client';

const CITY_COORDS = {
  'Douala': [4.0511, 9.7679],
  'Yaoundé': [3.8480, 11.5021],
  'Bafoussam': [5.4737, 10.4179],
  'Bamenda': [5.9597, 10.1459],
  'Garoua': [9.3017, 13.3921],
  'Maroua': [10.5956, 14.3247],
  'Ngaoundéré': [7.3167, 13.5833],
  'Bertoua': [4.5772, 13.6846],
  'Ebolowa': [2.9167, 11.1500],
  'Kribi': [2.9500, 9.9000],
  'Limbe': [4.0225, 9.2136],
  'Buea': [4.1560, 9.2870],
  'Dschang': [5.4500, 10.0500],
  'Edéa': [3.8000, 10.1333],
  'Kumba': [4.6363, 9.4470],
  'Nkongsamba': [4.9547, 9.9401],
  'Foumban': [5.7263, 10.9166],
  'Bafang': [5.1667, 10.1833],
  'Mbouda': [5.6333, 10.2500],
  'Sangmélima': [2.9333, 11.9833],
};
const CAMEROON_CITIES = Object.keys(CITY_COORDS);

const GENDERS = [
  { value: '', label: 'Preferer ne pas dire' },
  { value: 'homme', label: 'Homme' },
  { value: 'femme', label: 'Femme' },
  { value: 'autre', label: 'Autre' },
];

function extractErrorMessage(err) {
  if (!err) return 'Erreur inconnue.';
  return (
    err?.response?.data?.error ||
    err?.data?.error ||
    err?.error ||
    err?.message ||
    (typeof err === 'string' ? err : null) ||
    'Erreur inconnue - regarde la console navigateur (F12) pour le detail technique.'
  );
}

function getCityCenter(cityName) {
  const match = Object.keys(CITY_COORDS).find((c) => c.toLowerCase() === (cityName || '').trim().toLowerCase());
  return match ? CITY_COORDS[match] : CITY_COORDS['Douala'];
}

function MapClickHandler({ onPick }) {
  useMapEvents({
    click(e) { onPick(e.latlng.lat, e.latlng.lng); },
  });
  return null;
}

// Recentre la carte des que la ville choisie change - meme si aucun quartier n'a ete trouve,
// on peut toujours placer son point manuellement dans la bonne ville.
function RecenterOnCity({ center }) {
  const map = useMap();
  useEffect(() => {
    map.flyTo(center, 13, { duration: 0.6 });
  }, [center, map]);
  return null;
}

const CreateProfileModal = ({ userId, onCreated, onClose }) => {
  const { theme } = useTheme();

  const [avatarPreview, setAvatarPreview] = useState(null);
  const [username, setUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState(null);
  const [city, setCity] = useState('Douala');
  const [district, setDistrict] = useState('');
  const [districtSuggestions, setDistrictSuggestions] = useState([]);
  const [showDistrictSuggestions, setShowDistrictSuggestions] = useState(false);
  const [gender, setGender] = useState('');
  const [bio, setBio] = useState('');
  const [position, setPosition] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const usernameDebounce = useRef(null);
  const districtDebounce = useRef(null);
  const avatarInputRef = useRef(null);

  const cityCenter = getCityCenter(city);

  useEffect(() => {
    if (usernameDebounce.current) clearTimeout(usernameDebounce.current);
    const clean = username.trim();
    if (clean.length < 3) {
      setUsernameStatus(null);
      return;
    }
    setUsernameStatus('checking');
    usernameDebounce.current = setTimeout(async () => {
      try {
        const res = await socialApi.checkUsername(clean);
        setUsernameStatus(res.available ? 'available' : 'taken');
      } catch (err) {
        setUsernameStatus(null);
      }
    }, 400);
  }, [username]);

  useEffect(() => {
    if (districtDebounce.current) clearTimeout(districtDebounce.current);
    if (district.trim().length < 2) {
      setDistrictSuggestions([]);
      return;
    }
    districtDebounce.current = setTimeout(async () => {
      try {
        const res = await apiClient.get(`/endpoints/quartiers-search.php?q=${encodeURIComponent(district)}&ville=${encodeURIComponent(city)}`);
        setDistrictSuggestions(res.results || []);
      } catch (err) {
        setDistrictSuggestions([]);
      }
    }, 300);
  }, [district, city]);

  const handleSelectDistrict = (quartier) => {
    setDistrict(quartier.nom);
    setPosition({ lat: quartier.latitude, lng: quartier.longitude });
    setShowDistrictSuggestions(false);
    setDistrictSuggestions([]);
  };

  const handleCityChange = (value) => {
    setCity(value);
    setPosition(null);
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setError("Impossible d'obtenir ta position, place le point manuellement sur la carte."),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const handleMapPick = useCallback((lat, lng) => setPosition({ lat, lng }), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!userId) {
      setError("Impossible de recuperer ton identifiant de compte. Deconnecte-toi puis reconnecte-toi, ensuite reessaie.");
      return;
    }
    if (username.trim().length < 3) return setError('Le nom d\'utilisateur doit faire au moins 3 caracteres.');
    if (usernameStatus === 'taken') return setError('Ce nom d\'utilisateur est deja utilise. Choisis un autre nom.');
    if (!city.trim()) return setError('La ville est requise.');
    if (!district.trim()) return setError('Le quartier est requis.');

    const finalPosition = position || { lat: cityCenter[0], lng: cityCenter[1] };

    setSaving(true);
    try {
      await socialApi.createProfile({
        user_id: userId,
        username: username.trim(),
        avatar: avatarPreview || null,
        gender: gender || null,
        city: city.trim(),
        district: district.trim(),
        latitude: finalPosition.lat,
        longitude: finalPosition.lng,
        bio: bio.trim() || null,
      });

      onCreated({
        user_id: userId,
        username: username.trim(),
        avatar: avatarPreview || null,
        city: city.trim(),
        district: district.trim(),
        gender,
        bio,
        latitude: finalPosition.lat,
        longitude: finalPosition.lng,
      });
    } catch (err) {
      console.error('Erreur creation profil social:', err);
      setError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const styles = {
    overlay: { position: 'fixed', inset: 0, backgroundColor: theme.bg, zIndex: 3000, overflowY: 'auto' },
    wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh', padding: '20px', gap: '14px', maxWidth: '480px', margin: '0 auto' },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    title: { fontSize: '20px', fontWeight: '700', color: theme.textPrimary },
    closeBtn: { background: 'none', border: 'none', fontSize: '20px', color: theme.textSecondary, cursor: 'pointer' },
    subtitle: { fontSize: '13px', color: theme.textSecondary, textAlign: 'center', marginBottom: '6px' },
    avatarSection: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' },
    avatarCircle: {
      width: '96px', height: '96px', borderRadius: '50%', overflow: 'hidden',
      backgroundColor: theme.bgCard, border: `2px solid ${theme.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px', cursor: 'pointer',
    },
    avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
    avatarBtn: { fontSize: '13px', fontWeight: '700', color: theme.gold, background: 'none', border: 'none', cursor: 'pointer' },
    field: { display: 'flex', flexDirection: 'column', gap: '6px', position: 'relative' },
    label: { fontSize: '13px', fontWeight: '600', color: theme.textSecondary },
    inputRow: { display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '10px' },
    atSign: { fontSize: '15px', fontWeight: '700', color: theme.gold },
    input: { flex: 1, border: 'none', outline: 'none', backgroundColor: 'transparent', color: theme.textPrimary, fontSize: '15px', padding: '10px 0' },
    plainInput: { padding: '12px 14px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '10px', color: theme.textPrimary, fontSize: '15px', outline: 'none' },
    select: { padding: '12px 14px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '10px', color: theme.textPrimary, fontSize: '15px', outline: 'none' },
    textarea: { padding: '12px 14px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '10px', color: theme.textPrimary, fontSize: '14px', outline: 'none', minHeight: '70px', resize: 'vertical', fontFamily: 'inherit' },
    hint: { fontSize: '11px', color: theme.textSecondary },
    statusOk: { fontSize: '11px', color: '#48BB78', fontWeight: '600' },
    statusTaken: { fontSize: '11px', color: '#F56565', fontWeight: '600' },
    suggestions: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '10px', marginTop: '4px', zIndex: 50, overflow: 'hidden' },
    suggestionItem: { padding: '10px 12px', fontSize: '13px', color: theme.textPrimary, cursor: 'pointer', borderBottom: `1px solid ${theme.borderSoft}` },
    mapWrap: { height: '220px', borderRadius: '12px', overflow: 'hidden', border: `1px solid ${theme.border}` },
    geoBtn: { alignSelf: 'flex-start', padding: '8px 12px', fontSize: '12px', fontWeight: '700', color: theme.gold, background: 'none', border: `1px solid ${theme.gold}`, borderRadius: '8px', cursor: 'pointer' },
    errorBox: { padding: '12px', backgroundColor: 'rgba(245,101,101,0.1)', border: '1px solid rgba(245,101,101,0.3)', borderRadius: '10px', fontSize: '13px', color: '#F56565', textAlign: 'center' },
    submitBtn: { padding: '14px', backgroundColor: theme.gold, border: 'none', borderRadius: '10px', color: '#FFFFFF', fontSize: '15px', fontWeight: '700', cursor: 'pointer', marginTop: '6px' },
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.wrap}>
        <div style={styles.header}>
          <div style={styles.title}>✨ Cree ton profil social</div>
          {onClose && <button style={styles.closeBtn} onClick={onClose}>✕</button>}
        </div>
        <div style={styles.subtitle}>Rapide et simple — modifiable plus tard</div>

        {!userId && (
          <div style={styles.errorBox}>
            Ton identifiant de compte est introuvable. Deconnecte-toi puis reconnecte-toi avant de reessayer.
          </div>
        )}

        <form style={{ display: 'flex', flexDirection: 'column', gap: '14px' }} onSubmit={handleSubmit}>
          <div style={styles.avatarSection}>
            <div style={styles.avatarCircle} onClick={() => avatarInputRef.current && avatarInputRef.current.click()}>
              {avatarPreview ? <img src={avatarPreview} alt="Avatar" style={styles.avatarImg} /> : '📷'}
            </div>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleAvatarChange}
            />
            <button type="button" style={styles.avatarBtn} onClick={() => avatarInputRef.current && avatarInputRef.current.click()}>
              {avatarPreview ? 'Changer la photo' : 'Ajouter une photo de profil'}
            </button>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Nom d'utilisateur</label>
            <div style={styles.inputRow}>
              <span style={styles.atSign}>@</span>
              <input
                style={styles.input}
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.replace(/\s/g, '').toLowerCase())}
                placeholder="yvan237"
                required
              />
            </div>
            {usernameStatus === 'checking' && <span style={styles.hint}>Verification...</span>}
            {usernameStatus === 'available' && <span style={styles.statusOk}>✓ Disponible</span>}
            {usernameStatus === 'taken' && <span style={styles.statusTaken}>Ce nom d'utilisateur est deja utilise. Choisis un autre nom.</span>}
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Ville</label>
            <input
              style={styles.plainInput}
              list="social-city-list"
              value={city}
              onChange={(e) => handleCityChange(e.target.value)}
              placeholder="Ex: Douala"
            />
            <datalist id="social-city-list">
              {CAMEROON_CITIES.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Quartier</label>
            <input
              style={styles.plainInput}
              type="text"
              value={district}
              onChange={(e) => { setDistrict(e.target.value); setShowDistrictSuggestions(true); }}
              onFocus={() => setShowDistrictSuggestions(true)}
              placeholder="Ex: Bonapriso, ou un nouveau nom"
            />
            <span style={styles.hint}>Pas trouve ? Tape simplement le nom — place ton point sur la carte ci-dessous.</span>
            {showDistrictSuggestions && districtSuggestions.length > 0 && (
              <div style={styles.suggestions}>
                {districtSuggestions.map((q) => (
                  <div key={q.id} style={styles.suggestionItem} onClick={() => handleSelectDistrict(q)}>
                    📍 {q.nom} — {q.ville}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Position sur la carte (facultatif, ajustable)</label>
            <button type="button" style={styles.geoBtn} onClick={handleUseMyLocation}>
              📍 Utiliser ma position actuelle
            </button>
            <div style={styles.mapWrap}>
              <MapContainer center={position ? [position.lat, position.lng] : cityCenter} zoom={13} style={{ width: '100%', height: '100%' }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <RecenterOnCity center={position ? [position.lat, position.lng] : cityCenter} />
                <MapClickHandler onPick={handleMapPick} />
                {position && <Marker position={[position.lat, position.lng]} />}
              </MapContainer>
            </div>
            <span style={styles.hint}>
              La carte suit automatiquement la ville choisie — clique dessus pour placer ton quartier, meme s'il n'a pas ete trouve.
            </span>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Genre (facultatif)</label>
            <select style={styles.select} value={gender} onChange={(e) => setGender(e.target.value)}>
              {GENDERS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Bio (facultatif)</label>
            <textarea style={styles.textarea} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Quelques mots sur toi..." maxLength={280} />
          </div>

          {error && <div style={styles.errorBox}>{error}</div>}

          <button style={styles.submitBtn} type="submit" disabled={saving || usernameStatus === 'taken'}>
            {saving ? 'Creation...' : 'Creer mon profil'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateProfileModal;